import React, { useEffect, useRef, useState } from "react";

// components
import MainContent from "./Main.content";
import Layout from "../Layout/Layout.container";

// Env
import { TRACY_BACKOFFICE_BASE_URL } from "../../../redux/actions/dashboard/env.variables";

// services
import AxumAccountsService from "../../../services/axumaccounts.service";
import TracyAuthService from "../../../services/tracyauth.service";
import AuthService from "../../../services/auth.service";

// utils
import { getCurrentTab, getDefaultDateObjs, dateToShortDateString, getDefaultDates } from "../../../utils/functions";
import { buildKendoFilter, Selector } from "../../../utils/kendoUtils";
import { getIndicators } from "../../../utils/commonFetchers";
import { readClassicView } from "../../../config/classicView";
import { defaultFilters, FilterValues } from "../../../config/appliedFilters";

// types
import { DateRange } from "../../../types/common";
import { Origin } from "../../../types/origins";
import { Leader } from "../../../types/leaders";
import { Supervisor } from "../../../types/supervisors";
import { Seller } from "../../../types/sellers";
import { State } from "../../../types/states";
import { Channel } from "../../../types/channels";
import { Client } from "../../../types/clients";
import { OperationType } from "../../../types/operationTypes";

interface Props {
    getOrdersRequest: (data: any, params: any, entity: string) => Promise<void>;
    getSellerStatsRequest: (data: any, params: any) => Promise<void>;
    login: (token: string, refreshToken: string) => void;
    logout: () => void;
    auth: any;
    ordersTotal: number;
    fetchingOrders: boolean;
    sellersTotal: number;
}

// Componente de Loading
const LoadingSpinner = () => (
    <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        zIndex: 9999
    }}>
        <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "20px"
        }}>
            <div style={{
                width: "50px",
                height: "50px",
                border: "5px solid #f3f3f3",
                borderTop: "5px solid #3498db",
                borderRadius: "50%",
                animation: "spin 1s linear infinite"
            }} />
            <p style={{
                fontSize: "16px",
                color: "#666",
                margin: 0
            }}>Cargando...</p>
        </div>
        <style>
            {`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}
        </style>
    </div>
);

// Componente de Error para Axia V2
const AxiaV2ErrorComponent = ({ error, onRetry }: { error: string; onRetry: () => void }) => (
    <div>
        {!window.orders360Embedded && <Layout />}

        <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "calc(100vh - 80px)", // Ajustar según altura del Layout
            gap: "20px",
            padding: "40px",
            textAlign: "center"
        }}>
            <div style={{
                backgroundColor: "#fee",
                border: "1px solid #fcc",
                borderRadius: "8px",
                padding: "20px",
                maxWidth: "600px",
                width: "100%"
            }}>
                <h2 style={{ color: "#c33", marginBottom: "16px" }}>
                    Error en Axia V2
                </h2>
                <p style={{ color: "#666", marginBottom: "20px", lineHeight: "1.5" }}>
                    {error}
                </p>
                <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                    <button
                        onClick={onRetry}
                        style={{
                            padding: "12px 24px",
                            backgroundColor: "#3498db",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "14px"
                        }}
                    >
                        Reintentar
                    </button>
                </div>
            </div>
        </div>
    </div>
);

const Main = (props: Props) => {
    const [isAxiaV2, setIsAxiaV2] = useState<boolean | null>(null);
    const [tracyUrl, setTracyUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [authError, setAuthError] = useState<boolean>(false);
    const [axiaV2Error, setAxiaV2Error] = useState<string | null>(null);

    const [rangeDate, setRangeDate] = useState<DateRange>(getDefaultDateObjs());
    const [idSourceFilter, setIdSourceFilter] = useState<string>("");
    const [originsSelected, setOriginsSelected] = useState<Origin[]>([]);
    const [leadersSelected, setLeadersSelected] = useState<Leader[]>([]);
    const [supervisorsSelected, setSupervisorsSelected] = useState<Supervisor[]>([]);
    const [sellersSelected, setSellersSelected] = useState<Seller[]>([]);
    const [statesSelected, setStatesSelected] = useState<State[]>([]);
    const [channelsSelected, setChannelsSelected] = useState<Channel[]>([]);
    const [clientsSelected, setClientsSelected] = useState<Client[]>([]);
    const [operationTypesSelected, setOperationTypesSelected] = useState<OperationType[]>([]);
    const [activeFilters, setActiveFilters] = useState<any>({
        filter: null,
        skip: 0,
        take: 20,
        sort: null,
    });
    const [filtersChanged, setFiltersChanged] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState<FilterValues>(() => defaultFilters(getDefaultDateObjs()));
    const [reintentosQuantity, setReintentosQuantity] = useState<number>(0);
    const [pendienteDescargaQuantity, setPendienteDescargaQuantity] = useState<number>(0);

    const initRender = useRef(true);
    const shownRange = useRef<DateRange | null>(null);
    const axiaVersionCache = useRef<{ organizationCode: string; version: string | null; timestamp: number } | null>(null);

    const initializeApp = async () => {
        try {
            setIsLoading(true);
            setAuthError(false);
            setAxiaV2Error(null);

            // Primero ejecutamos checkAuth y esperamos a que termine
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.has('token')) {
                // Si hay token en la URL, esperamos a que se procese
                const token = urlParams.get('token') ?? "";
                const refreshToken = urlParams.get('refreshToken') ?? "";
                props.login(token, refreshToken);
                sessionStorage.setItem('notiserviceJWT', token);

                // Damos un pequeño tiempo para que el localStorage se actualice
                await new Promise(resolve => setTimeout(resolve, 100));

                if (!window.orders360Embedded) {
                    window.location.href = window.location.href.split("?")[0];
                    return; // No continuamos si vamos a recargar
                }
            } else if (!props.auth || !props.auth.isLoggedIn) {
                // Si no hay token y no está logueado, redirigimos
                window.location.href = AuthService.loginUrl;
                return;
            }

            // Ahora cargamos la configuración de Axia
            const userStr = localStorage.getItem("user");
            if (!userStr) {
                setIsAxiaV2(false);
                setIsLoading(false);
                return;
            }

            let user;
            try {
                user = JSON.parse(userStr);
            } catch (e) {
                console.error("Error parsing user from localStorage:", e);
                localStorage.removeItem("user");
                setIsAxiaV2(false);
                setIsLoading(false);
                setAuthError(true);
                return;
            }

            const organizationCode = user.organizationCode;
            const token = user.token;

            // Validamos que tengamos los datos necesarios
            if (!organizationCode || !token) {
                console.error("Missing organizationCode or token");
                localStorage.removeItem("user");
                setIsAxiaV2(false);
                setIsLoading(false);
                setAuthError(true);
                return;
            }

            // Verificamos si tenemos una versión cacheada reciente (5 minutos)
            const cacheValid = axiaVersionCache.current &&
                axiaVersionCache.current.organizationCode === organizationCode &&
                (Date.now() - axiaVersionCache.current.timestamp) < 5 * 60 * 1000;

            let versionAxia: string | null = null;
            if (cacheValid && axiaVersionCache.current) {
                versionAxia = axiaVersionCache.current.version;
            } else {
                try {
                    versionAxia = await AxumAccountsService.getVersionAxiaSetting(token);
                    // Solo cacheamos si obtuvimos una versión válida
                    if (versionAxia !== null) {
                        axiaVersionCache.current = {
                            organizationCode,
                            version: versionAxia,
                            timestamp: Date.now()
                        };
                    }
                } catch (error) {
                    console.error("Error fetching Axia version:", error);
                    // Si falla la llamada, intentamos usar el cache aunque esté expirado
                    if (axiaVersionCache.current && axiaVersionCache.current.organizationCode === organizationCode) {
                        versionAxia = axiaVersionCache.current.version;
                    } else {
                        // Si no hay cache, asumimos V1
                        setIsAxiaV2(false);
                        setIsLoading(false);
                        return;
                    }
                }
            }

            // La grilla de Tracy se muestra si el usuario es Axia V2. Si NO es V2, se valida el
            // TracySyncConfig y se usa Tracy solo cuando Enable === true && SyncOrders === true
            // (se prioriza AxAccounts#Axia.TracySyncConfig sobre Axia.TracySyncConfig).
            let shouldUseTracy = versionAxia === '2';

            if (!shouldUseTracy) {
                try {
                    const tracySyncConfig = await AxumAccountsService.getTracySyncConfig(token);
                    shouldUseTracy = tracySyncConfig?.Enable === true && tracySyncConfig?.SyncOrders === true;
                } catch (error) {
                    console.error("Error fetching TracySyncConfig:", error);
                }
            }

            if (shouldUseTracy) {
                // El usuario debe usar Tracy
                try {
                    const tracyJwt = await TracyAuthService.login(token);
                    if (tracyJwt) {
                        const iframeUrl = `${TRACY_BACKOFFICE_BASE_URL}/Authentication/LoginOrdersEmbedded?token=${encodeURIComponent(tracyJwt)}`;
                        setTracyUrl(iframeUrl);
                        setIsAxiaV2(true);
                    } else {
                        // Error: no se pudo obtener JWT de Tracy
                        console.error("Failed to obtain Tracy JWT");
                        setIsAxiaV2(true); // Es V2 pero con error
                        setAxiaV2Error("No se pudo autenticar con Tracy. Por favor, verifique sus credenciales e inténtelo nuevamente.");
                    }
                } catch (error) {
                    console.error("Error logging into Tracy:", error);
                    // Error en el login de Tracy
                    setIsAxiaV2(true); // Es V2 pero con error
                    setAxiaV2Error(`Error al conectar con Tracy: ${error instanceof Error ? error.message : 'Error desconocido'}. Por favor, inténtelo nuevamente.`);
                }
            } else {
                setIsAxiaV2(false);
            }
        } catch (error) {
            console.error("Error initializing app:", error);
            setAxiaV2Error(`Error al inicializar la aplicación: ${error instanceof Error ? error.message : 'Error desconocido'}`);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        initializeApp();
    }, []); // Solo ejecutamos una vez al montar el componente

    useEffect(() => {
        // Solo cargar indicadores si NO es Axia V2 o si es V2 pero hay error
        if (isAxiaV2 === false || (isAxiaV2 === true && axiaV2Error)) {
            getIndicators().then(x => {
                setReintentosQuantity(x.reintentosQuantity);
                setPendienteDescargaQuantity(x.pendienteDescargaQuantity);
            });

            const interval = setInterval(() => {
                getIndicators().then(x => {
                    setReintentosQuantity(x.reintentosQuantity);
                    setPendienteDescargaQuantity(x.pendienteDescargaQuantity);
                });
            }, 900000); // 15 minutes

            return () => clearInterval(interval);
        }
    }, [activeFilters, isAxiaV2, axiaV2Error]);

    useEffect(() => {
        if (shownRange.current === rangeDate) {
            shownRange.current = null;
        } else if (!initRender.current) {
            setFiltersChanged(true);
        } else {
            initRender.current = false;
        }
    }, [
        rangeDate,
        idSourceFilter,
        originsSelected,
        leadersSelected,
        supervisorsSelected,
        sellersSelected,
        statesSelected,
        channelsSelected,
        clientsSelected,
        operationTypesSelected
    ]);

    // Si hay error de autenticación general
    if (authError) {
        return (
            <div style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100vh",
                gap: "20px"
            }}>
                <h2>Error de autenticación</h2>
                <p>Por favor, inicie sesión nuevamente.</p>
                <button onClick={() => {
                    localStorage.clear();
                    sessionStorage.clear();
                    window.location.reload();
                }}>
                    Recargar
                </button>
            </div>
        );
    }

    const pending = isLoading || isAxiaV2 === null;

    // Mostrar el loading mientras se determina qué versión usar
    if (pending && readClassicView()) {
        return <LoadingSpinner />;
    }

    // Si es Axia V2 pero hay error, mostrar error específico
    if (isAxiaV2 === true && axiaV2Error) {
        return (
            <AxiaV2ErrorComponent
                error={axiaV2Error}
                onRetry={initializeApp}
            />
        );
    }

    const draftFilters: FilterValues = {
        rangeDate,
        idSourceFilter,
        originsSelected,
        leadersSelected,
        supervisorsSelected,
        sellersSelected,
        statesSelected,
        channelsSelected,
        clientsSelected,
        operationTypesSelected,
    };

    const sendFilters = (skip = 0, take = 20, sort: any = null, range: DateRange = rangeDate) => {
        const selectors: Selector[] = [
            { values: originsSelected, key: "originSystemCode", frontKey: "shortDescription" },
            { values: leadersSelected, key: "leaderId" },
            { values: supervisorsSelected, key: "supervisorId" },
            { values: sellersSelected, key: "sellerId" },
            { values: statesSelected, key: "erpState", frontKey: "state" },
            { values: channelsSelected, key: "clientChannel" },
            { values: clientsSelected, key: "clientId" },
            { values: operationTypesSelected, key: "axumOperationTypeCode" },
            { values: idSourceFilter ? [{ idSource: idSourceFilter }] : [], key: "idSource", operator: "contains" },
        ];

        const filters = buildKendoFilter(selectors);

        const dateParams = {
            dateSince: dateToShortDateString(range.start) || getDefaultDates().start,
            dateUntil: dateToShortDateString(range.end) || getDefaultDates().end,
        };

        setFiltersChanged(false);
        setAppliedFilters({ ...draftFilters, rangeDate: range });

        const dataQuery = {
            filter: filters,
            skip: typeof skip === "number" ? skip : 0,
            take: take,
            sort: sort,
        };

        setActiveFilters(dataQuery);

        if (getCurrentTab() === 'seller_stats') {
            props.getSellerStatsRequest(dataQuery, dateParams);
        } else {
            props.getOrdersRequest(dataQuery, dateParams, getCurrentTab());
        }
    };

    const resetFilters = () => {
        setRangeDate(getDefaultDateObjs());
        setOriginsSelected([]);
        setLeadersSelected([]);
        setSupervisorsSelected([]);
        setSellersSelected([]);
        setStatesSelected([]);
        setChannelsSelected([]);
        setClientsSelected([]);
        setOperationTypesSelected([]);
        setIdSourceFilter("");
        setFiltersChanged(false);
        setAppliedFilters(defaultFilters(getDefaultDateObjs()));

        const dateParams = {
            dateSince: getDefaultDates().start,
            dateUntil: getDefaultDates().end,
        };

        const dataQuery = {
            filter: null,
            skip: 0,
            take: 20,
            sort: null,
        };

        setActiveFilters(dataQuery);

        if (getCurrentTab() === 'seller_stats') {
            props.getSellerStatsRequest(dataQuery, dateParams);
        } else {
            props.getOrdersRequest(dataQuery, dateParams, getCurrentTab());
        }
    };

    const discardFilters = () => {
        setRangeDate(appliedFilters.rangeDate);
        setIdSourceFilter(appliedFilters.idSourceFilter);
        setOriginsSelected(appliedFilters.originsSelected);
        setLeadersSelected(appliedFilters.leadersSelected);
        setSupervisorsSelected(appliedFilters.supervisorsSelected);
        setSellersSelected(appliedFilters.sellersSelected);
        setStatesSelected(appliedFilters.statesSelected);
        setChannelsSelected(appliedFilters.channelsSelected);
        setClientsSelected(appliedFilters.clientsSelected);
        setOperationTypesSelected(appliedFilters.operationTypesSelected);
    };

    const presetRange = (range: DateRange) => {
        shownRange.current = range;
        setRangeDate(range);
        setAppliedFilters(applied => ({ ...applied, rangeDate: range }));
    };

    const showRange = (range: DateRange) => {
        shownRange.current = range;
        setRangeDate(range);
        sendFilters(0, activeFilters.take, null, range);
    };

    // Solo renderizar MainContent si NO es Axia V2 o si es V2 exitoso
    return (
        <MainContent
            pending={pending}
            // Props de Axia V2
            isAxiaV2={isAxiaV2 === true}
            tracyUrl={tracyUrl}
            // Props existentes
            rangeDate={rangeDate}
            setRangeDate={setRangeDate}
            idSourceFilter={idSourceFilter}
            setIdSourceFilter={setIdSourceFilter}
            originsSelected={originsSelected}
            leadersSelected={leadersSelected}
            supervisorsSelected={supervisorsSelected}
            sellersSelected={sellersSelected}
            statesSelected={statesSelected}
            channelsSelected={channelsSelected}
            clientsSelected={clientsSelected}
            operationTypesSelected={operationTypesSelected}
            setOriginsSelected={setOriginsSelected}
            setLeadersSelected={setLeadersSelected}
            setSupervisorsSelected={setSupervisorsSelected}
            setSellersSelected={setSellersSelected}
            setStatesSelected={setStatesSelected}
            setChannelsSelected={setChannelsSelected}
            setClientsSelected={setClientsSelected}
            setOperationTypesSelected={setOperationTypesSelected}
            sendFilters={sendFilters}
            resetFilters={resetFilters}
            showRange={showRange}
            discardFilters={discardFilters}
            presetRange={presetRange}
            appliedFilters={appliedFilters}
            activeFilters={activeFilters}
            filtersChanged={filtersChanged}
            pendienteDescargaQuantity={pendienteDescargaQuantity}
            reintentosQuantity={reintentosQuantity}
            setPendienteDescargaQuantity={setPendienteDescargaQuantity}
            setReintentosQuantity={setReintentosQuantity}
            ordersTotal={props.ordersTotal}
            fetchingOrders={props.fetchingOrders}
            sellersTotal={props.sellersTotal}
        />
    );
};

export default Main;